// File: components/dashboard/QuickQuiz.tsx
"use client";

import { useState } from "react";
import {
  Zap, CheckCircle2, XCircle, Bookmark, BookmarkCheck,
  Loader2, ChevronRight,
} from "lucide-react";
import RichTextRenderer from "@/components/editor/RichTextRenderer";
import { fetchRandomQuestion, saveQuestion, logQuizAnswer } from "@/actions/quiz";
import type { Question } from "@/actions/quiz";

// ── Skeleton ──────────────────────────────────────────────────
export function QuickQuizSkeleton() {
  return (
    <div className="bg-white border border-border rounded-xl shadow-card p-4 animate-pulse">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-4 h-4 rounded bg-muted" />
        <div className="h-3.5 w-24 rounded bg-muted" />
      </div>
      <div className="h-4 w-full rounded bg-muted mb-1.5" />
      <div className="h-4 w-3/4 rounded bg-muted mb-4" />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {[0, 1, 2, 3].map(i => <div key={i} className="h-11 rounded-xl bg-muted" />)}
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────
export default function QuickQuiz({ initialQuestion }: { initialQuestion: Question }) {
  const [question, setQuestion] = useState<Question>(initialQuestion);
  const [selected, setSelected] = useState<string[]>([]);  // array để support multiple_choice
  const [showAnswer, setShowAnswer] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");
  const [seenIds, setSeenIds] = useState<string[]>([initialQuestion.id]);
  const [isLoading, setIsLoading] = useState(false);

  const isMultiple = question.type === "multiple_choice";

  // ── Select option ──────────────────────────────────────────
  function handleSelect(optId: string) {
    if (showAnswer) return;

    if (isMultiple) {
      // Multiple: toggle, chưa check ngay
      setSelected(prev =>
        prev.includes(optId) ? prev.filter(x => x !== optId) : [...prev, optId]
      );
    } else {
      // Single: check ngay
      const correct = question.correct_answers.includes(optId);
      setSelected([optId]);
      setShowAnswer(true);
      setIsCorrect(correct);
      // Log fire-and-forget, không await để không chặn UI
      logQuizAnswer(question.id, correct).catch(() => {});
      window.dispatchEvent(new CustomEvent("jb:question-answered", { detail: { count: 1 } }));
    }
  }

  // ── Confirm multiple choice ────────────────────────────────
  function handleConfirm() {
    if (selected.length === 0 || showAnswer) return;
    const correctSet = new Set(question.correct_answers);
    const selectedSet = new Set(selected);
    const correct =
      correctSet.size === selectedSet.size &&
      [...correctSet].every(x => selectedSet.has(x));
    setShowAnswer(true);
    setIsCorrect(correct);
    logQuizAnswer(question.id, correct).catch(() => {});
    window.dispatchEvent(new CustomEvent("jb:question-answered", { detail: { count: 1 } }));
  }

  // ── Next question ───────────────────────────────────────────
  async function handleNext() {
    if (isLoading) return;
    setIsLoading(true);
    try {
      const next = await fetchRandomQuestion(seenIds.slice(-50));
      if (next) {
        setQuestion(next);
        setSeenIds(ids => [...ids, next.id].slice(-50));
      }
      setSelected([]);
      setShowAnswer(false);
      setIsCorrect(false);
      setSaved(false);
      setSaveMsg("");
    } catch (err) {
      console.error("[QuickQuiz] handleNext error:", err);
    } finally {
      setIsLoading(false);
    }
  }

  // ── Save question ──────────────────────────────────────────
  async function handleSave() {
    if (saved || isLoading) return;
    setIsLoading(true);
    try {
      const res = await saveQuestion(question.id);
      if (res.success) setSaved(true);
      setSaveMsg(res.message);
      setTimeout(() => setSaveMsg(""), 2500);
    } catch (err) {
      console.error("[QuickQuiz] handleSave error:", err);
    } finally {
      setIsLoading(false);
    }
  }

  // ── Option styles ──────────────────────────────────────────
  function getOptionCls(optId: string): string {
    const base = "flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl border text-left transition-all text-[12px] w-full";
    const isSelected = selected.includes(optId);
    const isRight = question.correct_answers.includes(optId);

    if (!showAnswer) {
      if (isSelected) return `${base} border-teal-500 bg-teal-50 text-teal-800 cursor-pointer`;
      return `${base} border-border bg-white hover:border-teal-500/50 hover:bg-teal-50/30 cursor-pointer text-foreground/80`;
    }

    // Đã show answer
    if (isRight) return `${base} border-emerald-400 bg-emerald-50 text-emerald-800 ring-1 ring-emerald-300/30`;
    if (isSelected) return `${base} border-red-400 bg-red-50 text-red-700 ring-1 ring-red-300/30`;
    return `${base} border-border/40 bg-white opacity-40 text-foreground/40`;
  }

  function getCircleCls(optId: string): string {
    const base = "w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-extrabold shrink-0";
    const isSelected = selected.includes(optId);
    const isRight = question.correct_answers.includes(optId);

    if (!showAnswer) {
      if (isSelected) return `${base} bg-teal-600 text-white`;
      return `${base} bg-muted text-muted-foreground`;
    }
    if (isRight) return `${base} bg-emerald-500 text-white`;
    if (isSelected) return `${base} bg-red-500 text-white`;
    return `${base} bg-muted text-muted-foreground/40`;
  }

  return (
    <div className="bg-white border border-border rounded-xl shadow-card p-4 relative overflow-hidden">
      {/* Loading overlay khi fetch next */}
      {isLoading && (
        <div className="absolute inset-0 bg-white/70 backdrop-blur-[1px] z-10 flex items-center justify-center rounded-xl">
          <Loader2 className="w-5 h-5 text-teal-600 animate-spin" />
        </div>
      )}

      {/* Header */}
      <div className="flex items-center gap-2 mb-3">
        <Zap className="w-4 h-4 text-amber-500 shrink-0" />
        <span className="text-[12px] font-bold text-foreground">Trả lời nhanh</span>
        <span className={`ml-auto inline-flex items-center px-2 py-[2px] rounded-full text-[9px] font-bold border ${
          question.difficulty === "easy"   ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
          question.difficulty === "hard"   ? "bg-red-50 text-red-600 border-red-200" :
                                             "bg-amber-50 text-amber-600 border-amber-200"
        }`}>
          {question.difficulty === "easy" ? "Dễ" : question.difficulty === "hard" ? "Khó" : "Trung bình"}
        </span>
      </div>

      {/* Question */}
      <div className="text-[13px] font-semibold text-foreground leading-relaxed mb-3">
        <RichTextRenderer content={question.content} />
      </div>

      {/* Type hint cho multiple choice */}
      {isMultiple && !showAnswer && (
        <div className="mb-2 text-[11px] text-teal-600 font-semibold">
          💡 Chọn tất cả đáp án đúng
        </div>
      )}

      {/* Options */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
        {question.options.map(opt => (
          <button
            key={opt.id}
            onClick={() => handleSelect(opt.id)}
            disabled={showAnswer || isLoading}
            className={getOptionCls(opt.id)}
          >
            <div className={getCircleCls(opt.id)}>
              {showAnswer && question.correct_answers.includes(opt.id)
                ? <CheckCircle2 className="w-3 h-3" />
                : showAnswer && selected.includes(opt.id) && !question.correct_answers.includes(opt.id)
                ? <XCircle className="w-3 h-3" />
                : opt.id}
            </div>
            <RichTextRenderer content={opt.text} className="flex-1 leading-snug" />
          </button>
        ))}
      </div>

      {/* Confirm button cho multiple_choice */}
      {isMultiple && !showAnswer && selected.length > 0 && (
        <button
          onClick={handleConfirm}
          className="w-full py-2.5 rounded-xl bg-teal-800 text-white text-[12px] font-bold hover:bg-teal-700 transition-colors mb-3"
        >
          Kiểm tra đáp án
        </button>
      )}

      {/* Result feedback */}
      {showAnswer && (
        <div className={`flex items-start gap-2 px-3 py-2.5 rounded-xl text-[12px] font-semibold mb-3 ${
          isCorrect
            ? "bg-emerald-50 text-emerald-800 border border-emerald-200"
            : "bg-red-50 text-red-700 border border-red-200"
        }`}>
          {isCorrect
            ? <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5 text-emerald-600" />
            : <XCircle className="w-4 h-4 shrink-0 mt-0.5 text-red-500" />}
          <div className="flex-1">
            <span>
              {isCorrect
                ? "Chính xác!"
                : `Chưa đúng. Đáp án: ${question.correct_answers.join(", ")}`}
            </span>
            {question.explanation && (
              <p className={`text-[11px] font-normal mt-0.5 ${isCorrect ? "text-emerald-700/70" : "text-red-600/70"}`}>
                {question.explanation}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Save toast */}
      {saveMsg && (
        <div className="px-3 py-2 rounded-lg bg-teal-50 border border-teal-200 text-[11px] font-semibold text-teal-700 mb-3">
          {saveMsg}
        </div>
      )}

      {/* Footer toolbar */}
      <div className="flex items-center gap-2 pt-1 border-t border-border/50">
        <button
          onClick={handleSave}
          disabled={saved || isLoading}
          className={`flex items-center justify-center gap-1.5 px-3.5 py-2 rounded-xl text-[11px] font-bold border transition-all ${
            saved
              ? "bg-teal-50 text-teal-700 border-teal-200 cursor-default"
              : "bg-white text-muted-foreground border-border hover:bg-teal-50 hover:text-teal-700 hover:border-teal-200"
          }`}
        >
          {saved
            ? <><BookmarkCheck className="w-3.5 h-3.5" /><span>Đã lưu</span></>
            : <><Bookmark className="w-3.5 h-3.5" /><span>Lưu</span></>}
        </button>

        <button
          onClick={handleNext}
          disabled={isLoading}
          className="ml-auto flex items-center justify-center gap-1 px-4 py-2 rounded-xl bg-teal-800 text-white text-[11px] font-bold hover:bg-teal-700 disabled:opacity-50 transition-all"
        >
          <span>Câu tiếp</span>
          <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}